import { useCallback, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { ChevronDown, ChevronRight, FileSignature, FolderOpen, History, Info, ShieldQuestion, Sparkles, Users } from "lucide-react";
import { useApi, useAuth, useCaseGroup, useInvalidateOn, type AiProposal, type PagedResult, type Persona, type WorkflowInstanceView } from "@ncp/api-client";
import { formatDate, useLocale, useTranslation } from "@ncp/i18n";
import { ActionScope, Badge, Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger, Popover, PopoverContent, PopoverTrigger, Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle, Skeleton, StatusBadge, Tooltip, SourceText, useSave } from "@ncp/ui";
import type { CaseDetail } from "./api";
import type { ReviewView } from "./review/api";
import { StageStepper } from "./process/ProcessBar";
import { processPhases, type PhaseKey } from "./process/phases";
import { CaseActions, LinkedCases } from "./CaseActions";
import { InterestBanner } from "./InterestBanner";
import { sectionOf } from "../registers/CaseRegister";
import { RiskLevel } from "../../components/RiskLevel";
import { ReviewTab } from "./review/ReviewTab";
import { FileTab, type FileView } from "./tabs/FileTab";
import { stageViews, type StageView } from "./tabs/AnalysisHubTab";
import { DecisionTab } from "./tabs/DecisionTab";
import { HistoryTab, type HistoryView } from "./tabs/HistoryTab";
import { CaseAssistant } from "./CaseAssistant";
import { ParallelRunButton } from "./ParallelRunPanel";
import { StageChecklistCard } from "./stage/StageChecklistCard";
import { StagePrimaryAction, type InterestHold } from "./stage/StagePrimaryAction";
import { NextStepSurface } from "./stage/nextStep";
import { PhaseRecord } from "./stage/PhaseRecord";

const FILE_SECTIONS: FileView[] = ["documents", "correspondence", "parties"];
const HISTORY_SECTIONS: HistoryView[] = ["timeline", "reviews", "process", "agents", "audit"];
type Panel = "file" | "history";

/**
 * Addresses of the previous workspace (five tabs, then sub-views) mapped onto what replaced them: a stage page, a workbench on it, or
 * one of the two side panels. Older links, bookmarks and the other applications' deep links keep landing in the right place.
 */
function legacyTarget(tab: string, view: string | null): { panel?: Panel; section?: string; view?: string } | null {
  switch (tab) {
    case "review": case "overview": return {};
    case "file": return { panel: "file", section: view ?? undefined };
    case "documents": case "correspondence": case "parties": return { panel: "file", section: tab };
    case "history": return { panel: "history", section: view ?? undefined };
    case "workflow": return { panel: "history", section: "process" };
    case "agents": case "audit": case "reviews": case "timeline": return { panel: "history", section: tab };
    case "analysis": return { view: view && view !== "none" ? view : "analysis" };
    case "investigation-report": return { view: "report" };
    case "decision": return { view: "decision" };
    default: return { view: tab };
  }
}

/**
 * The case, navigated by its stages alone. The header carries the file's one next step; the stepper shows the stages of this file; the
 * stage page shows what is left, the stage review and the workbenches the stage needs, all on one page. The case file and the history
 * open beside it in side panels. A stage already passed opens the record of what was concluded there.
 */
export function CaseWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params, setSearchParams] = useSearchParams();
  // Every address here (criterion, stage, panel, workbench) is a place on this one page: the router must not reset the window to the top
  // on them; the page brings into view whatever it opens.
  const setParams = useCallback<typeof setSearchParams>((next, options) => setSearchParams(next, { preventScrollReset: true, ...options }), [setSearchParams]);

  const rawTab = params.get("tab");
  useEffect(() => {
    if (!rawTab) return;
    const target = legacyTarget(rawTab, params.get("view"));
    setParams((p) => {
      p.delete("tab"); p.delete("view");
      if (target?.panel) { p.set("panel", target.panel); if (target.section) p.set("section", target.section); }
      if (target?.view) p.set("view", target.view);
      return p;
    }, { replace: true });
  }, [rawTab, params, setParams]);

  useCaseGroup(id);
  useInvalidateOn(["case.updated", "case.transitioned", "proposal.created", "proposal.updated", "agent.run", "review.updated", "dealwatch.updated"], [["case", id], ["case-workflow", id], ["case-proposals", id], ["cases"], ["case-events", id], ["case-letters", id], ["case-rfis", id], ["case-decisions", id], ["case-documents", id], ["deal-watch", "by-case", id], ["case-review", id]]);

  const detail = useQuery({ queryKey: ["case", id], queryFn: () => api.get<CaseDetail>(`/api/cases/${id}`) });
  const workflow = useQuery({ queryKey: ["case-workflow", id], queryFn: () => api.get<WorkflowInstanceView>(`/api/cases/${id}/workflow`) });
  const proposals = useQuery({ queryKey: ["case-proposals", id], queryFn: () => api.get<PagedResult<AiProposal>>("/api/ai/proposals", { caseId: id, pageSize: 100 }) });
  const review = useQuery({ queryKey: ["case-review", id, locale], queryFn: () => api.get<ReviewView>(`/api/cases/${id}/review`, { locale }) });
  const interests = useQuery({ queryKey: ["case-interests", id], queryFn: () => api.get<{ required: boolean; mine: { status: string } | null }>(`/api/cases/${id}/interests`) });
  const personas = useQuery({ queryKey: ["personas"], queryFn: () => api.get<Persona[]>("/api/auth/staff/personas") });
  // A market-study file runs its Data Bank study: the file names it, and the Market Study Studio opens it.
  const fileStudy = useQuery({
    queryKey: ["databank", "studies", "by-file", id],
    queryFn: () => api.get<Array<{ id: string; title: string; titleAr?: string | null; status: string }>>("/api/databank/studies", { caseId: id! }),
    enabled: detail.data?.summary.type === "MarketStudy",
  });
  const assign = useSave({ mutationFn: (assigneeUserId: string | null) => api.post(`/api/cases/${id}/assign`, { assigneeUserId, reason: "Assigned from case workspace" }), saved: t("misc.assignedTo"), onSuccess: () => { void qc.invalidateQueries({ queryKey: ["case", id] }); } });

  const qualification = useMemo<{ verdict?: string; notificationRequired?: boolean; flaggedForScreening?: boolean } | null>(() => {
    try { return (JSON.parse(detail.data?.metadataJson ?? "{}") as { qualification?: { verdict?: string; notificationRequired?: boolean; flaggedForScreening?: boolean } }).qualification ?? null; } catch { return null; }
  }, [detail.data?.metadataJson]);
  const phases = useMemo(() => (workflow.data ? processPhases(workflow.data) : []), [workflow.data]);

  const panel = (params.get("panel") as Panel | null) ?? null;
  const section = params.get("section");
  const view = params.get("view");
  const currentPhase = phases.find((p) => p.standing === "current")?.key ?? phases[phases.length - 1]?.key ?? null;
  const reachable = (k: string | null): k is PhaseKey => !!k && phases.some((p) => p.key === k && (p.standing === "done" || p.standing === "current"));
  const requested = params.get("stage");
  const phase = reachable(requested) ? requested : currentPhase;
  const onCurrent = phase === currentPhase;

  const setStage = (k: PhaseKey) => setParams((p) => { if (k === currentPhase) p.delete("stage"); else p.set("stage", k); p.delete("view"); p.delete("criterion"); return p; });
  const openPanel = useCallback((next: Panel, nextSection?: string, docId?: string) => setParams((p) => {
    p.set("panel", next);
    if (nextSection) p.set("section", nextSection); else p.delete("section");
    if (docId) p.set("doc", docId); else p.delete("doc");
    return p;
  }), [setParams]);
  const closePanel = () => setParams((p) => { p.delete("panel"); p.delete("section"); p.delete("doc"); return p; });
  const setSection = (s: string | null) => setParams((p) => { if (s) p.set("section", s); else p.set("section", "none"); return p; }, { replace: true });
  const clearStep = useCallback(() => setParams((p) => { p.delete("step"); return p; }, { replace: true }), [setParams]);
  const setCriterion = useCallback((key: string | null) => setParams((p) => { if (key) p.set("criterion", key); else p.delete("criterion"); p.delete("stage"); return p; }, { replace: true }), [setParams]);

  const views = useMemo<StageView[]>(() => {
    if (!detail.data) return [];
    const canDecide = !!user && ["Minister", "Undersecretary", "AssistantUndersecretary", "Director", "Admin"].some((r) => user.roles.includes(r));
    const c = detail.data.summary;
    return stageViews(detail.data, proposals.data?.items ?? [], t, () => <DecisionTab caseId={c.id} caseType={c.type} state={c.state} proposals={proposals.data?.items ?? []} userRoles={user?.roles ?? []} canDecide={canDecide} />);
  }, [detail.data, proposals.data, t, user]);

  /**
   * Every place the case refers to is one address: a panel section (file:documents, history:process …), a workbench (analysis:report,
   * decision) or the review itself. A workbench of another stage opens on the stage that needs it when the file has reached it, and
   * otherwise on the current stage, below its own work.
   */
  const open = useCallback((surface: string, recordId?: string) => {
    const [area, sub] = surface.split(":");
    if (area === "file") { openPanel("file", sub ?? "documents", recordId); return; }
    if (area === "history") { openPanel("history", sub ?? "timeline"); return; }
    if (area === "review") { setParams((p) => { p.delete("panel"); p.delete("stage"); p.delete("view"); return p; }); return; }
    const key = area === "decision" ? "decision" : sub ?? views.find((v) => currentPhase && v.phases.includes(currentPhase))?.key ?? views[0]?.key;
    const target = views.find((v) => v.key === key);
    const home = target && currentPhase && target.phases.includes(currentPhase) ? currentPhase : target?.phases.find((k) => reachable(k)) ?? currentPhase;
    setParams((p) => { p.delete("panel"); if (key) p.set("view", key); if (home && home !== currentPhase) p.set("stage", home); else p.delete("stage"); return p; });
  }, [openPanel, setParams, views, currentPhase]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!view) return;
    const handle = window.setTimeout(() => document.getElementById(`view-${view}`)?.scrollIntoView({ block: "start", behavior: "smooth" }), 250);
    return () => window.clearTimeout(handle);
  }, [view, phase]);

  if (detail.isLoading || !detail.data) return <div className="space-y-4"><Skeleton className="h-12 w-1/2" /><Skeleton className="h-96" /></div>;
  const c = detail.data.summary;
  const representation = detail.data.representation ?? null;
  const isEc = c.type === "EconomicConcentration";
  // Until the review of the stage the file has just entered arrives, the one it left is still cached: its next step is not offered.
  const checklist = review.data?.stageKey === c.state ? review.data.checklist ?? null : null;
  const closed = ["Start", "End"].includes(workflow.data?.definition.nodes.find((n) => n.key === workflow.data?.currentState)?.kind ?? "Start");
  const declared = interests.data?.mine?.status;
  const held: InterestHold | null = closed || !interests.data ? null
    : declared === "Recused" ? "recused" : declared === "Pending" ? "pending" : !declared && interests.data.required ? "declare" : null;
  const primary = checklist?.primary ?? null;
  const primaryCriterion = primary?.kind === "criterion" && primary.target ? primary.target.slice("criterion:".length) : null;
  const selectedCriterion = params.get("criterion") ?? primaryCriterion;
  const primaryInCard = !held && onCurrent && !panel && !!primaryCriterion && selectedCriterion === primaryCriterion;
  const primarySurface = !held && onCurrent && !panel && primary?.kind === "surface" ? primary.surface ?? null : null;

  const phaseViews = phase ? views.filter((v) => v.phases.includes(phase)) : [];
  const extra = view && onCurrent && !phaseViews.some((v) => v.key === view) ? views.find((v) => v.key === view) : undefined;
  const shownViews = extra ? [...phaseViews, extra] : phaseViews;
  const surfaceShown = !!primarySurface && shownViews.some((v) => primarySurface === `analysis:${v.key}` || primarySurface === v.key);
  const nextStep = surfaceShown && primarySurface ? { surface: primarySurface, criterion: primary?.target?.startsWith("criterion:") ? primary.target.slice("criterion:".length) : null } : null;

  const home = sectionOf(c.type);
  const details: Array<{ label: string; value: React.ReactNode }> = [
    { label: t("misc.type"), value: t(`caseTypes.${c.type}`) },
    // One risk reading everywhere: the level of the register, from the file's one risk score (7.1); the triage priority is not a risk.
    ...(isEc ? [{ label: t("backoffice:cases.columns.risk"), value: <RiskLevel score={c.riskScore} /> }] : []),
    ...(c.organizationName ? [{ label: t("misc.organization"), value: (locale === "ar" && c.organizationNameAr) || c.organizationName }] : []),
    { label: t("misc.createdAt"), value: formatDate(c.createdAt, locale) },
    ...(isEc && qualification && (c.state === "Screening" || c.state === "Returned" || qualification.flaggedForScreening)
      ? [{ label: t("backoffice:screening.badge"), value: <Tooltip content={t("backoffice:screening.badgeHint")}><Badge variant={qualification.verdict === "Constitutes" ? "warning" : "danger"}><ShieldQuestion /> {c.state === "Returned" ? t("backoffice:screening.returned") : t(`backoffice:workspace.verdict.${qualification.verdict ?? "Unassessed"}`, { defaultValue: qualification.verdict })}</Badge></Tooltip> }]
      : []),
    ...(c.type === "MarketStudy" ? [{
      label: t("backoffice:processes.study.fileStudy"),
      value: fileStudy.data?.[0]
        ? <button type="button" className="text-start underline-offset-4 hover:underline" onClick={() => navigate(`/data-bank?tab=studio&study=${fileStudy.data![0]!.id}`)}><SourceText text={locale === "ar" && fileStudy.data[0].titleAr ? fileStudy.data[0].titleAr : fileStudy.data[0].title} /> · {t(`backoffice:dataBank.studyStatus.${fileStudy.data[0].status}`, { defaultValue: fileStudy.data[0].status })}</button>
        : <span className="text-muted-foreground">{t("backoffice:processes.study.fileNoStudy")}</span>,
    }] : []),
    ...(representation && representation.basis !== "Member"
      ? [{ label: t("backoffice:representatives.badge"), value: <Badge variant={representation.mandateStatus === "Verified" ? "success" : representation.mandateStatus === "Pending" ? "warning" : "danger"} className="cursor-pointer" onClick={() => open("file:parties")}><FileSignature /> {representation.basis === "Mandate" ? t(`backoffice:representatives.status.${representation.mandateStatus ?? "Pending"}`, { defaultValue: representation.mandateStatus ?? "" }) : t("backoffice:representatives.status.None")}</Badge> }]
      : []),
  ];

  const onPrimary = (kind: string, target?: string | null) => {
    if (kind === "criterion" && target) { setParams((p) => { p.delete("panel"); p.delete("stage"); p.delete("view"); p.set("criterion", target.slice("criterion:".length)); return p; }); window.setTimeout(() => document.getElementById("criterion-card")?.scrollIntoView({ block: "start", behavior: "smooth" }), 150); return; }
    if (kind === "issue" && target) {
      const issue = review.data?.issues.find((i) => `issue:${i.id}` === target);
      setParams((p) => { p.delete("panel"); p.delete("stage"); if (issue?.criterionKey) p.set("criterion", issue.criterionKey); p.set("step", target); return p; });
      window.setTimeout(() => document.getElementById("stage-issues")?.scrollIntoView({ block: "center", behavior: "smooth" }), 150);
      return;
    }
    if (kind === "surface" && target) {
      const key = target.split(":")[1] ?? target;
      if (surfaceShown) { document.getElementById(`view-${key}`)?.scrollIntoView({ block: "start", behavior: "smooth" }); return; }
      // The stage's next step is done here, below the stage's own work, even on a workbench that belongs to an earlier stage.
      setParams((p) => { p.delete("panel"); p.delete("stage"); p.set("view", key); return p; });
      return;
    }
    if (kind === "conclude") setParams((p) => { p.delete("panel"); p.delete("stage"); p.set("step", "conclude"); return p; });
  };

  return (
    <ActionScope emphasis="secondary">
      <header className="mb-4">
        <nav aria-label={t("backoffice:workspace.breadcrumb")} className="flex items-center gap-1 text-xs text-muted-foreground">
          <button type="button" onClick={() => navigate(home.href)} className="transition-colors hover:text-aegold-800">{t(home.labelKey)}</button>
          <ChevronRight className="size-3 rtl:rotate-180" aria-hidden />
          <span className="font-mono text-foreground" dir="ltr">{c.caseNumber}</span>
          {c.isTest ? (
            <Tooltip content={t("backoffice:workspace.testFileHint")}>
              <Badge variant="warning" size="sm" className="ms-2">{t("backoffice:workspace.testFile", { date: c.testAsOf ? formatDate(c.testAsOf, locale) : "—" })}</Badge>
            </Tooltip>
          ) : null}
        </nav>
        <div className="mt-1.5 flex flex-wrap items-start gap-x-4 gap-y-3">
          <h1 className="min-w-0 flex-1 basis-80 text-2xl font-semibold leading-tight text-aeblack-950 sm:text-[1.75rem]"><SourceText text={locale === "ar" && c.titleAr ? c.titleAr : c.title} /></h1>
          <div className="flex shrink-0 items-start gap-2">
            <StagePrimaryAction checklist={checklist} inCard={primaryInCard || surfaceShown} held={held} onAct={onPrimary} />
            <CaseActions caseId={c.id} caseNumber={c.caseNumber} workflow={workflow.data} isTest={c.isTest} />
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <StatusBadge status={c.status} label={t(`status.${c.status}`, { defaultValue: c.status })} />
          {c.priority === "High" || c.priority === "Critical" ? <StatusBadge status={c.priority} label={t(`enums.priority.${c.priority}`, { defaultValue: c.priority })} /> : null}
          <LinkedCases caseId={c.id} parentCaseId={c.parentCaseId ?? null} />
          <Popover>
            <PopoverTrigger asChild><Button size="sm" variant="ghost" className="h-7 px-2"><Info /> {t("backoffice:workspace.details")}</Button></PopoverTrigger>
            <PopoverContent align="start" className="w-80 p-0">
              <p className="border-b border-sand-200 px-4 py-2.5 text-xs font-semibold text-sand-800">{t("backoffice:workspace.details")}</p>
              <dl className="divide-y divide-sand-100 px-4 text-sm">
                {details.map((d) => <div key={d.label} className="flex items-start justify-between gap-4 py-2"><dt className="shrink-0 text-muted-foreground">{d.label}</dt><dd className="min-w-0 text-end font-medium text-aeblack-900">{d.value}</dd></div>)}
              </dl>
            </PopoverContent>
          </Popover>
          <span className="mx-1 hidden h-5 w-px bg-sand-200 sm:block" aria-hidden />
          <Button size="sm" variant="ghost" className="h-7 px-2" data-panel-trigger="file" aria-haspopup="dialog" aria-expanded={panel === "file"} onClick={() => openPanel("file", "documents")}><FolderOpen /> {t("backoffice:stage.panels.file")}</Button>
          <Button size="sm" variant="ghost" className="h-7 px-2" data-panel-trigger="history" aria-haspopup="dialog" aria-expanded={panel === "history"} onClick={() => openPanel("history", "timeline")}><History /> {t("backoffice:stage.panels.history")}</Button>
          <CaseAssistant key={c.id} caseId={c.id} caseNumber={c.caseNumber} onOpenDocument={(docId) => open("file:documents", docId)} />
          {c.isTest || params.get("parallel") === "1" ? <ParallelRunButton caseId={c.id} /> : null}
          {isEc ? <Tooltip content={t("backoffice:agentic.entryHint")}><Button size="sm" variant="ghost" className="h-7 px-2 text-aegold-800" onClick={() => navigate(`/concentration/agentic/${c.id}`)}><Sparkles /> {t("backoffice:agentic.entry")}</Button></Tooltip> : null}
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button size="sm" variant="ghost"><Users /> {(locale === "ar" && c.assigneeNameAr) || c.assigneeName || t("misc.unassigned")} <ChevronDown /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
              <DropdownMenuLabel>{t("misc.assignedTo")}</DropdownMenuLabel>
              {personas.data?.map((p) => <DropdownMenuItem key={p.id} onSelect={() => assign.mutate(p.id)}>{locale === "ar" ? p.displayNameAr : p.displayName} <span className="ms-auto text-xs text-muted-foreground">{locale === "ar" ? p.titleAr : p.title}</span></DropdownMenuItem>)}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <InterestBanner caseId={c.id} closed={closed} />

      <div className="mb-5">
        <StageStepper detail={detail.data} workflow={workflow.data} selected={phase} onSelect={setStage} />
      </div>

      {onCurrent ? (
        <div className="space-y-5">
          {checklist ? <StageChecklistCard caseId={c.id} checklist={checklist} review={review.data} onSelectCriterion={(k) => onPrimary("criterion", `criterion:${k}`)} onSelectIssue={(target) => onPrimary("issue", target)} onOpenDocuments={() => open("file:documents")} onOpenSurface={open} /> : null}
          <ReviewTab detail={detail.data} workflow={workflow.data} proposals={proposals.data?.items ?? []} selectedKey={selectedCriterion} primaryCriterion={primaryInCard ? primaryCriterion : null} onSelect={setCriterion} onOpenSurface={open} pendingStep={params.get("step")} onStepHandled={clearStep} />
          <NextStepSurface.Provider value={nextStep}>
            {shownViews.map((v) => <StageWorkbench key={v.key} view={v} phase={phase ?? undefined} />)}
          </NextStepSurface.Provider>
        </div>
      ) : phase ? (
        <div className="space-y-5">
          <PhaseRecord caseId={c.id} phase={phases.find((p) => p.key === phase)!} />
          {shownViews.map((v) => <StageWorkbench key={v.key} view={v} phase={phase ?? undefined} />)}
        </div>
      ) : null}

      <Sheet open={panel === "file"} onOpenChange={(o) => { if (!o) closePanel(); }}>
        <SheetContent width="max-w-5xl" emphasis="secondary">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2"><FolderOpen className="size-5 text-aegold-700" /> {t("backoffice:stage.panels.file")}</SheetTitle>
            <SheetDescription><SourceText text={locale === "ar" && c.titleAr ? c.titleAr : c.title} /> · <span dir="ltr">{c.caseNumber}</span></SheetDescription>
          </SheetHeader>
          <SheetBody>
            <FileTab detail={detail.data} view={section === "none" ? null : FILE_SECTIONS.includes(section as FileView) ? (section as FileView) : "documents"} onViewChange={setSection} documentId={params.get("doc")} onOpenDocument={(docId) => openPanel("file", "documents", docId)} onOpenReview={closePanel} />
          </SheetBody>
        </SheetContent>
      </Sheet>
      <Sheet open={panel === "history"} onOpenChange={(o) => { if (!o) closePanel(); }}>
        <SheetContent width="max-w-3xl" emphasis="secondary">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2"><History className="size-5 text-aegold-700" /> {t("backoffice:stage.panels.history")}</SheetTitle>
            <SheetDescription><SourceText text={locale === "ar" && c.titleAr ? c.titleAr : c.title} /> · <span dir="ltr">{c.caseNumber}</span></SheetDescription>
          </SheetHeader>
          <SheetBody>
            <HistoryTab caseId={c.id} caseNumber={c.caseNumber} workflow={workflow.data} clocks={detail.data.clocks} view={section === "none" ? null : HISTORY_SECTIONS.includes(section as HistoryView) ? (section as HistoryView) : "timeline"} onViewChange={setSection} />
          </SheetBody>
        </SheetContent>
      </Sheet>
    </ActionScope>
  );
}

/** A workbench on the stage page: a heading and its content, open. */
function StageWorkbench({ view, phase }: { view: StageView; phase?: PhaseKey }) {
  const Icon = view.icon;
  return (
    <section id={`view-${view.key}`} className="scroll-mt-24 space-y-3" aria-labelledby={`view-${view.key}-title`}>
      <h2 id={`view-${view.key}-title`} className="flex items-center gap-2 border-b border-sand-200 pb-2 font-heading text-lg font-semibold text-aeblack-950">
        <Icon className="size-5 text-aegold-700" aria-hidden /> {view.label}
        {view.badge ? <Badge variant="ai" size="sm">{view.badge}</Badge> : null}
      </h2>
      {view.render(phase)}
    </section>
  );
}
